import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

const overlayVariants = {
  closed: {
    opacity: 0,
    backdropFilter: 'blur(0px)',
    transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
  },
  open: {
    opacity: 1,
    backdropFilter: 'blur(14px)',
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

const panelVariants = {
  closed: {
    opacity: 0,
    scale: 0.78,
    y: 56,
    rotateX: 8,
    filter: 'blur(10px)',
    transition: {
      duration: 0.42,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  open: {
    opacity: 1,
    scale: 1,
    y: 0,
    rotateX: 0,
    filter: 'blur(0px)',
    transition: {
      type: 'spring',
      damping: 24,
      stiffness: 260,
      mass: 0.9,
    },
  },
};

const PublicModal = ({
  isOpen,
  onClose,
  onExited,
  title,
  children,
  wide,
  bodyClassName,
  subtitle,
  cleanCard = false,
}) => {
  const closeBtnRef = useRef(null);
  const [mounted, setMounted] = useState(isOpen);
  const openRef = useRef(isOpen);

  useEffect(() => {
    openRef.current = isOpen;
    if (isOpen) {
      setMounted(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const t = requestAnimationFrame(() => closeBtnRef.current?.focus());
      return () => {
        cancelAnimationFrame(t);
        window.removeEventListener('keydown', onKey);
      };
    }
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted, isOpen, onClose]);

  const finishClose = () => {
    document.body.style.overflow = '';
    setMounted(false);
    onExited?.();
  };

  const handlePanelComplete = () => {
    if (!openRef.current) finishClose();
  };

  if (!mounted) return null;

  return createPortal(
    <motion.div
      className="modal-overlay modal-overlay--animated"
      role="dialog"
      aria-modal="true"
      aria-labelledby="public-modal-title"
      variants={overlayVariants}
      initial="closed"
      animate={isOpen ? 'open' : 'closed'}
      onClick={onClose}
      style={{ perspective: 1200 }}
    >
      <motion.div
        className={`modal-content${wide ? ' modal-wide' : ''}${cleanCard ? ' modal-clean-card' : ''}`}
        variants={panelVariants}
        initial="closed"
        animate={isOpen ? 'open' : 'closed'}
        onAnimationComplete={handlePanelComplete}
        onClick={(e) => e.stopPropagation()}
        style={{ transformOrigin: 'center center' }}
      >
        {cleanCard ? (
          <motion.button
            ref={closeBtnRef}
            type="button"
            className="modal-close-floating"
            onClick={onClose}
            aria-label="Close"
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 420, damping: 16 }}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              zIndex: 10100,
              background: 'rgba(0, 0, 0, 0.05)',
              border: 'none',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#333'
            }}
          >
            <X size={22} />
          </motion.button>
        ) : (
          <div className="modal-header">
            <div className="modal-header-text">
              <h2 id="public-modal-title">{title}</h2>
              {subtitle && <p className="modal-subtitle">{subtitle}</p>}
            </div>
            <motion.button
              ref={closeBtnRef}
              type="button"
              className="modal-close"
              onClick={onClose}
              aria-label="Close"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 420, damping: 16 }}
            >
              <X size={22} />
            </motion.button>
          </div>
        )}
        <div className={`modal-body${bodyClassName ? ` ${bodyClassName}` : ''}`}>{children}</div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default PublicModal;
